import { createFeatureSelector, createSelector } from "@ngrx/store";
import {
  IAddBetAmount,
  IAllEventsNameAndId,
  IEventTrackRaceState,
  IReplays,
  ISaveBet,
  ISelectedRace,
  ISelectedRaceAllOddsMatrixes,
  ISelectedRaceMatrix,
  ISelectedRacePoolsTotals,
  ISelectedRaceRaceResult,
  ISelectedRaceWillPays
} from "./wager.models";
import { IWagerReducer, WAGER_FEATURE_KEY, eventTrackRaceAdapter } from "./wager.reducer";
import { IGetEventsDetailsResponse, TError } from "@amwager/util-ts";


// Lookup the 'Wager' feature state managed by NgRx
export const selectWagerState = createFeatureSelector<IWagerReducer>(
  WAGER_FEATURE_KEY
);


/**
 * Event Track Race
 */
export const getSelectEventTrackRaceState = createSelector(
  selectWagerState,
  (state: IWagerReducer) => state?.eventTrackRace
);

export const isEventTrackRaceRequestingAPI = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): boolean | undefined => eventTrackRace?.isRequestingAPI
);

export const getEventTrackRaceLoaded = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): boolean | undefined => eventTrackRace?.isLoaded
);

export const getEventTrackRaceError = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): TError => eventTrackRace?.error
);

export const getSelectedEventTrackRaceId = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): string | undefined => eventTrackRace?.selectedId
);

export const getSelectedEventTrack = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): IGetEventsDetailsResponse | undefined =>
    eventTrackRace?.selectedEventTrack
);

export const getSelectedRace = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): ISelectedRace | undefined =>
    eventTrackRace?.selectedRace
);

export const getRaceResult = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): ISelectedRaceRaceResult | undefined =>
    eventTrackRace?.selectedRace?.raceResults
);

export const getRaceMatrix = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): ISelectedRaceMatrix | undefined =>
    eventTrackRace?.selectedRaceMatrix
);

export const getRacePoolsTotals = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): ISelectedRacePoolsTotals | undefined =>
    eventTrackRace?.selectedRacePoolsTotals
);

export const getRaceWillPays = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): ISelectedRaceWillPays | undefined =>
    eventTrackRace?.selectedRaceWillPays
);

export const getRaceAllOddsMatrixes = createSelector(
  getSelectEventTrackRaceState,
  (eventTrackRace: IEventTrackRaceState): {
    WIN?: ISelectedRaceAllOddsMatrixes,
    TRU?: ISelectedRaceAllOddsMatrixes
  } | undefined =>
    eventTrackRace?.selectedWINRaceAllOddsMatrixes
);



// get the selectors
const {
  selectIds,
  selectEntities,
  selectAll,
  selectTotal,
} = eventTrackRaceAdapter.getSelectors();

// select the array of EventTrackRace ids
export const getSelectEventTrackRaceIds = createSelector(
  getSelectEventTrackRaceState,
  selectIds
);

// select the dictionary of EventTrackRace entities
export const getSelectEventTrackRaceEntities = createSelector(
  getSelectEventTrackRaceState,
  selectEntities
);

// select the array of EventTrackRace
export const getSelectAllEventTrackRace = createSelector(
  getSelectEventTrackRaceState,
  selectAll
);

// select the total EventTrackRace count
export const getSelectEventTrackRaceTotal = createSelector(
  getSelectEventTrackRaceState,
  selectTotal
);

// get counter
export const getCounter = createSelector(
  getSelectEventTrackRaceState,
  // IGetBets
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.getCounters
);

/**
 * Get counters
 */
export const getCounters = createSelector(
  getCounter,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (getCounters: any | undefined): any | undefined => getCounters?.payload
);

// bet section
export const getBet = createSelector(
  getSelectEventTrackRaceState,
  // IGetBets
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.getBets
);

// export const getBets = createSelector(
//   getBet,
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   (bet: any | undefined): {response: any, isProcessing: boolean | undefined} => ({
//     response: bet?.response,
//     isProcessing: bet?.isProcessing
//   })
// );

/**
 * Get bets
 */
export const getBets = createSelector(
  getBet,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (getBets: any | undefined): any | undefined => getBets
);

/**
 * Submit saved bet
 */
export const submitSavedBetSuccess = createSelector(
  getBet,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (getBets: any | undefined): {response: any, isSuccess: boolean | undefined} => ({
    response: getBets?.response,
    isSuccess: getBets?.isSuccess
  })
);

/**
 * Remove saved bet
 */
export const removeSavedBetSuccess = createSelector(
  getBet,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (getBets: any | undefined): {response: any, isSuccess: boolean | undefined} => ({
    response: getBets?.response,
    isSuccess: getBets?.isSuccess
  })
);

/**
 * Cancel bet
 */
export const cancelBetSuccess = createSelector(
  getBet,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (getBets: any | undefined): {response: any, isSuccess: boolean | undefined} => ({
    response: getBets?.response,
    isSuccess: getBets?.isSuccess
  })
);

/**
 * Save bet
 */
export const saveBet = createSelector(
  getSelectEventTrackRaceState,
  (state: IEventTrackRaceState): ISaveBet | undefined => state?.saveBet
);

export const saveBetSuccess = createSelector(
  saveBet,
  (saveBet: ISaveBet | undefined):
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {response: any, isSuccess: boolean | undefined, isProcessing: boolean | undefined} => ({
    response: saveBet?.response,
    isSuccess: saveBet?.isSuccess,
    isProcessing: saveBet?.isProcessing
  })
);

/**
 * Bet now
 */
export const betAmount = createSelector(
  getSelectEventTrackRaceState,
  (state: IEventTrackRaceState): IAddBetAmount | undefined => state?.addBetAmount
);

export const betAmountSuccess = createSelector(
  betAmount,
  (betAmount: IAddBetAmount | undefined):
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  {response: any, isSuccess: boolean | undefined, isProcessing: boolean | undefined} => ({
    response: betAmount?.response,
    isSuccess: betAmount?.isSuccess,
    isProcessing: betAmount?.isProcessing
  })
);

/**
 * Bet preview
 */
export const getIsShowBetPreview = createSelector(
  getSelectEventTrackRaceState,
  (state: IEventTrackRaceState): boolean => state?.betPreview || false
);

/**
 * Get ticket
 *
 */
export const ticket = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.currentTicket
);

export const getTicket = createSelector(
  ticket,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (currentTicket: any | undefined): any | undefined => currentTicket?.payload
);

export const getGeoLocation = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.geolocation
);

export const getTriggerMatrixModal = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): string | undefined => state?.triggerMatrixModal
);

export const getToUpdateGeneralEvents = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): string | undefined => state?.toUpdateGeneralEvents
);



export const getAllEventsNameAndId = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): IAllEventsNameAndId | undefined => state?.allEventsNameAndId
);

export const getReplays = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): IReplays | undefined => state?.replays
);

export const getRunners = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): string[] | undefined => state?.runners
);

// live video
export const getLiveVideo = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.getEventLiveVideo
);

/**
 * Get live video
 */
export const getEventLiveVideo = createSelector(
  getLiveVideo,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (getEventLiveVideo: any | undefined): any | undefined => getEventLiveVideo
);

// replay info
export const getSelectedReplayInfo = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.replayInfo
);

// latest finished event
export const getLatestFinishedEvent = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.latestFinishedEvent
);

// default event
export const getDefaultEvent = createSelector(
  getSelectEventTrackRaceState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: IEventTrackRaceState): any | undefined => state?.isDefaultEvent
);
